import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, FileText, Users, Clock, Save, AlertTriangle, Check, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDisclaimerSettings({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState("");
  const [reacceptanceMonths, setReacceptanceMonths] = useState(6);
  const [report, setReport] = useState({ users: [], total_accepted: 0 });
  const [showReportDialog, setShowReportDialog] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/disclaimer/settings`, { withCredentials: true });
      setDisclaimerText(response.data.text || "");
      setReacceptanceMonths(response.data.reacceptance_months || 6);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load disclaimer settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/disclaimer/settings`, {
        text: disclaimerText,
        reacceptance_months: parseInt(reacceptanceMonths)
      }, { withCredentials: true });
      toast.success("Disclaimer settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save disclaimer settings");
    } finally {
      setSaving(false);
    }
  };

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/admin/disclaimer/report`, { withCredentials: true });
      setReport(response.data);
      setShowReportDialog(true);
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Failed to load acceptance report");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-SG", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link to="/admin?tab=tools">
            <Button variant="ghost" className="text-yellow-400 hover:text-yellow-300 mb-4">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Admin Tools
            </Button>
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-yellow-500/20 rounded-full">
                <FileText className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Merchant Disclaimer Settings</h1>
                <p className="text-slate-400">Manage the disclaimer shown to members before viewing merchant discounts</p>
              </div>
            </div>
            <Button 
              onClick={fetchReport}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              View Acceptance Report
            </Button>
          </div>
        </div>

        {/* Settings Card */}
        <Card className="bg-slate-900/50 border-white/5 p-6 space-y-6">
          {/* Re-acceptance Period */}
          <div>
            <label className="block text-sm font-medium text-white mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-yellow-500" />
              Re-acceptance Period (months)
            </label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                min="1"
                max="24"
                value={reacceptanceMonths}
                onChange={(e) => setReacceptanceMonths(e.target.value)}
                className="w-32"
              />
              <p className="text-sm text-slate-400">
                Members will need to re-accept the disclaimer after this period
              </p>
            </div>
          </div>

          {/* Disclaimer Text */}
          <div>
            <label className="block text-sm font-medium text-white mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-500" />
              Disclaimer Text
            </label>
            <Textarea
              value={disclaimerText}
              onChange={(e) => setDisclaimerText(e.target.value)}
              rows={12}
              className="bg-slate-800 border-slate-700 text-white"
              placeholder="Enter the disclaimer text that members must acknowledge..."
            />
            <p className="text-xs text-slate-400 mt-2">
              This text will be shown to members when they visit the Member Discounts page
            </p>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Preview</label>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 max-h-48 overflow-y-auto">
              <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                {disclaimerText || "No disclaimer text entered..."}
              </p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-white/5">
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-yellow-500 hover:bg-yellow-400 text-black font-semibold flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </Card>
      </div>

      {/* Acceptance Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-yellow-500" />
              Disclaimer Acceptance Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {/* Summary */}
            <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Total Accepted</p>
                  <p className="text-2xl font-bold text-yellow-500">{report.total_accepted}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Re-acceptance Period</p>
                  <p className="text-2xl font-bold text-white">{report.reacceptance_months} months</p>
                </div>
              </div>
            </div>

            {/* Users Table */}
            {report.users.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                No members have accepted the disclaimer yet.
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-400 uppercase px-3 py-2">
                  <div className="col-span-3">Member</div>
                  <div className="col-span-2">Membership #</div>
                  <div className="col-span-3">Accepted At</div>
                  <div className="col-span-2">Expires At</div>
                  <div className="col-span-2">Status</div>
                </div>
                {report.users.map((u) => (
                  <div 
                    key={u.id} 
                    className="grid grid-cols-12 gap-2 text-sm px-3 py-3 bg-slate-800/30 rounded-lg items-center"
                  >
                    <div className="col-span-3">
                      <p className="font-medium text-white truncate">{u.name}</p>
                      <p className="text-xs text-slate-400 truncate">{u.email}</p>
                    </div>
                    <div className="col-span-2 text-slate-300 font-mono">
                      {u.membership_number || "-"}
                    </div>
                    <div className="col-span-3 text-slate-300 text-xs">
                      {formatDate(u.disclaimer_accepted_at)}
                    </div>
                    <div className="col-span-2 text-slate-300 text-xs">
                      {formatDate(u.expires_at)}
                    </div>
                    <div className="col-span-2">
                      {u.is_expired ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                          <X className="w-3 h-3" /> Expired
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                          <Check className="w-3 h-3" /> Active
                        </span>
                      )}
                      {u.disclaimer_dont_show_again && (
                        <span className="block mt-1 text-xs text-slate-500">
                          (won&apos;t show again)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
